import clientApi from "./client";
import { Section, Field, LeadershipField, AlignsField, NumericField, CategoryField, StatusProgressField, MarketplaceField, SignalField, Suggestion } from "@/types/sections";
/**
 * New Step 1 API Response Types
 */
export interface KnowTheirPrioritiesApiResponse {
  company_snapshot?: {
    total_skus?: {
      value?: string;
      display?: string;
      growth?: {
        percent?: number;
        absolute?: number | null;
        comparison?: string | null;
      };
      sources?: Array<{
        name?: string | null;
        url?: string | null;
        title?: string | null;
        type?: string | null;
      }>;
    };
    revenue?: {
      value?: string;
      display?: string;
      growth?: {
        percent?: number;
        absolute?: number | null;
        comparison?: string | null;
      };
      sources?: Array<{
        name?: string | null;
        url?: string | null;
        title?: string | null;
        type?: string | null;
      }>;
    };
    industry?: {
      value?: string;
      display?: string;
      growth?: {
        percent?: number;
        absolute?: number | null;
        comparison?: string | null;
      };
      sources?: Array<{
        name?: string | null;
        url?: string | null;
        title?: string | null;
        type?: string | null;
      }>;
    };
    level?: {
      value?: string;
      display?: string;
      growth?: {
        percent?: number;
        absolute?: number | null;
        comparison?: string | null;
      };
      sources?: Array<{
        name?: string | null;
        url?: string | null;
        title?: string | null;
        type?: string | null;
      }>;
    };
    icp_fit?: {
      value?: string;
      display?: string;
      growth?: {
        percent?: number;
        absolute?: number | null;
        comparison?: string | null;
      };
      sources?: Array<{
        name?: string | null;
        url?: string | null;
        title?: string | null;
        type?: string | null;
      }>;
    };
    suggestions?: Array<{
      id?: string | number;
      title?: string;
      description?: string;
      is_liked?: boolean;
      is_disliked?: boolean;
      added_to_meeting?: boolean;
    }>;
  };
  marketplace_presence?: {
    marketplaces?: Array<{
      platform?: string;
      platform_meta?: Array<{
        region?: string;
        total_results?: number | null;
        type?: string | null;
      }>;
    }>;
    suggestions?: Array<{
      id?: string | number;
      title?: string;
      description?: string;
      is_liked?: boolean;
      is_disliked?: boolean;
      added_to_meeting?: boolean;
    }>;
  };
  signals_priorities?: {
    items?: Array<{
      id?: number;
      title?: string;
      description?: string;
      summary?: string;
      sources?: string[];
    }>;
    suggestions?: Array<{
      id?: string | number;
      title?: string;
      description?: string;
      is_liked?: boolean;
      is_disliked?: boolean;
      added_to_meeting?: boolean;
    }>;
  };
  meeting_attendees?: {
    attendees?: Array<{
      name?: string;
      img_url?: string;
      linkedin_url?: string;
      title?: string;
      designation?: string;
      focus?: string;
      tenure?: string;
      highlights?: Array<{
        content?: string;
        sources?: Array<{
          name?: string;
          url?: string;
          type?: string;
        }>;
      }>;
    }>;
    suggestions?: Array<{
      id?: string | number;
      title?: string;
      description?: string;
      is_liked?: boolean;
      is_disliked?: boolean;
      added_to_meeting?: boolean;
    }>;
  };
  rhithum_aligns?: {
    items?: Array<{
      id?: string | number;
      title?: string;
      description?: string;
      summary?: string;
      sources?: string[];
    }>;
    suggestions?: Array<{
      id?: string | number;
      title?: string;
      description?: string;
      is_liked?: boolean;
      is_disliked?: boolean;
      added_to_meeting?: boolean;
    }>;
  };
  leadership_team?: {
    team?: Array<{
      name?: string;
      img_url?: string;
      linkedin_url?: string;
      designation?: string;
      focus?: string;
      tenure?: string;
      highlights?: Array<{
        content?: string;
        sources?: Array<{
          name?: string;
          url?: string;
          type?: string;
        }>;
      }>;
    }>;
    suggestions?: Array<{
      id?: string | number;
      title?: string;
      description?: string;
      is_liked?: boolean;
      is_disliked?: boolean;
      added_to_meeting?: boolean;
    }>;
  };
}

/**
 * Service to fetch sections data
 */
export class SectionsService {
  /**
   * Fetch sections for a specific tab
   * @param tabId - The tab ID (can be string like "tab_1" or number)
   * @param meetingPrepId - The meeting prep ID (defaults to 1)
   * @param email - Reserved for future tabs (step 1 does not use it)
   * @param accountId - Reserved for future tabs (step 1 does not use it)
   * @returns Promise with sections data
   */
  static async getSections(
    tabId: string | number,
    meetingPrepId: number = 1,
    _email?: string | null,
    _accountId?: number
  ): Promise<Section[]> {
    // Extract numeric tab_id from string format like "tab_1" -> 1
    const numericTabId = typeof tabId === "string" 
      ? parseInt(tabId.replace("tab_", ""), 10) 
      : tabId;

    // For step 1 (tab_1), use new know_their_priorities API
    if (numericTabId === 1) {
      return this.getKnowTheirPrioritiesSections(meetingPrepId);
    }

    // Step 2 (tab_2) is loaded via `useStep2Sections` (paginated historical-context), not `getSections`.

    // For step 3+, return empty array (not implemented yet)
    return [];
  }

  /**
   * Build the Step 2 "Previous Conversations & Signals" section from timeline fields.
   * Used by `useStep2Sections` after `MeetingsService.transformHistoricalContextToFields`.
   */
  static buildHistoricalContextSection(fields: Field[]): Section {
    return {
      section_id: "historical_context",
      section_key: "historical_context",
      section_title: "Previous Conversations & Signals",
      section_order: 1,
      tab_id: "tab_2",
      fields,
      suggestions: [],
      layout: "list",
    };
  }

  /**
   * Get sections from know_their_priorities API for step 1
   * @param meetingPrepId - The meeting prep ID
   * @returns Promise with sections data
   */
  private static async getKnowTheirPrioritiesSections(
    meetingPrepId: number
  ): Promise<Section[]> {
    const response = await clientApi
      .get("api/meetings/know_their_priorites", {
        searchParams: {
          meeting_id: meetingPrepId.toString(),
        },
      })
      .json<KnowTheirPrioritiesApiResponse>();

    return this.transformKnowTheirPrioritiesResponse(response);
  }

  /**
   * Transform know_their_priorities API response to Section array
   */
  private static transformKnowTheirPrioritiesResponse(
    data: KnowTheirPrioritiesApiResponse
  ): Section[] {
    const sections: Section[] = [];
    let sectionOrder = 1;

    // 1. Company Snapshot
    const companySnapshotFields: Field[] = [];
    const companySnapshot = data.company_snapshot || {};
    
    // Total SKUs
    if (companySnapshot.total_skus) {
      const sku = companySnapshot.total_skus;
      const growth = sku.growth || {};
      const growthText = growth.absolute != null
        ? `+ ${growth.absolute.toLocaleString()} ${growth.comparison || ""}`
        : growth.comparison 
        ? `+ ${(growth.percent || 0)}% ${growth.comparison}`
        : "";
      
      companySnapshotFields.push({
        label: "Total SKUs",
        value: sku.display || "",
        trend: (growth.percent || 0) > 0 ? "positive" : (growth.percent || 0) < 0 ? "negative" : "neutral",
        subtext: growthText,
        field_id: `total_skus_${sectionOrder}`,
        trend_icon: (growth.percent || 0) > 0 ? "arrow-up" : (growth.percent || 0) < 0 ? "arrow-down" : "minus",
        trend_color: (growth.percent || 0) > 0 ? "green" : (growth.percent || 0) < 0 ? "red" : "gray",
        component_type: "numeric",
        sources: Array.isArray(sku.sources) && sku.sources.length > 0 ? sku.sources : undefined,
      } as NumericField);
    }

    // Revenue
    if (companySnapshot.revenue) {
      const revenue = companySnapshot.revenue;
      const growth = revenue.growth || {};
      const growthText = growth.comparison 
        ? `+ ${(growth.percent || 0)}% ${growth.comparison}`
        : "";
      
      companySnapshotFields.push({
        label: "Revenue",
        value: revenue.display || "",
        trend: (growth.percent || 0) > 0 ? "positive" : (growth.percent || 0) < 0 ? "negative" : "neutral",
        subtext: growthText,
        field_id: `revenue_${sectionOrder}`,
        trend_icon: (growth.percent || 0) > 0 ? "arrow-up" : (growth.percent || 0) < 0 ? "arrow-down" : "minus",
        trend_color: (growth.percent || 0) > 0 ? "green" : (growth.percent || 0) < 0 ? "red" : "gray",
        component_type: "currency",
        sources: Array.isArray(revenue.sources) && revenue.sources.length > 0 ? revenue.sources : undefined,
      } as NumericField);
    }

    // Industry
    if (companySnapshot.industry) {
      companySnapshotFields.push({
        label: "Industry",
        value: companySnapshot.industry.display || "",
        icon: "shopping-bag",
        field_id: `industry_${sectionOrder}`,
        component_type: "category",
        sources: Array.isArray(companySnapshot.industry.sources) && companySnapshot.industry.sources.length > 0 ? companySnapshot.industry.sources : undefined,
      } as CategoryField);
    }

    // Level
    if (companySnapshot.level) {
      const levelGrowth = companySnapshot.level.growth || {};
      companySnapshotFields.push({
        label: "Maturity Level",
        value: companySnapshot.level.display || "",
        color: "teal",
        field_id: `level_${sectionOrder}`,
        component_type: "status_progress",
        progress_value: levelGrowth.percent || 0,
        has_progress_ring: true,
        sources: Array.isArray(companySnapshot.level.sources) && companySnapshot.level.sources.length > 0 ? companySnapshot.level.sources : undefined,
      } as StatusProgressField);
    }

    // ICP Fit
    if (companySnapshot.icp_fit) {
      companySnapshotFields.push({
        label: "ICP Fit",
        value: companySnapshot.icp_fit.display || "",
        color: "teal",
        field_id: `icp_fit_${sectionOrder}`,
        component_type: "status_progress",
        progress_value: companySnapshot.icp_fit.display || 0,
        has_progress_ring: true,
        sources: Array.isArray(companySnapshot.icp_fit.sources) && companySnapshot.icp_fit.sources.length > 0 ? companySnapshot.icp_fit.sources : undefined,
      } as StatusProgressField);
    }

    sections.push({
      section_id: `company_snapshot_${sectionOrder}`,
      section_key: "company_snapshot",
      section_title: "Company Snapshot",
      section_order: sectionOrder++,
      tab_id: "tab_1",
      fields: companySnapshotFields,
      suggestions: this.transformSuggestions(companySnapshot.suggestions || []),
    });

    // 2. Marketplace Presence
    const marketplacePresence = data.marketplace_presence || {};


    // 3. Signals & Priorities
    const signalsPriorities = data.signals_priorities || {};
    const signalFields: Field[] = (Array.isArray(signalsPriorities.items) ? signalsPriorities.items : [])
      .filter((item) => item != null)
      .map((item, idx) => ({
      id: `signal_${sectionOrder}_${idx}`,
      signalId: item.id, // Store the actual signal ID from API
      has_detail: true,
      title: item.title || "",
      description: item.description || "",
      component_type: "signals",
    } as SignalField));

    sections.push({
      section_id: `signals_priorities_${sectionOrder}`,
      section_key: "signals_priorities",
      section_title: "Signals & Priorities",
      section_order: sectionOrder++,
      tab_id: "tab_1",
      fields: signalFields,
      suggestions: this.transformSuggestions(signalsPriorities.suggestions || []),
    });

    // 4. Meeting Attendees
    const meetingAttendees = data.meeting_attendees || {};
    const attendeeFields: Field[] = (Array.isArray(meetingAttendees.attendees) ? meetingAttendees.attendees : [])
      .filter((attendee) => attendee != null)
      .map((attendee, idx) => ({
        name: attendee.name || "",
        title: attendee.designation || attendee.title || "",
        tenure: attendee.tenure || "",
        insights: (Array.isArray(attendee.highlights) ? attendee.highlights : [])
          .filter((highlight) => highlight != null)
          .map((highlight, hIdx) => ({
            content: highlight.content || "",
            sources: (Array.isArray(highlight.sources) ? highlight.sources : [])
              .filter((source) => source != null)
              .map((source, sIdx) => ({
                url: source.url || "",
                title: source.name || "",
                type: source.type || "",
                source_id: `source_${sectionOrder}_${idx}_${hIdx}_${sIdx}`,
              })),
            insight_id: `insight_${sectionOrder}_${idx}_${hIdx}`,
          })),
        image_url: attendee.img_url || null,
        person_id: `person_${sectionOrder}_${idx}`,
        focus_area: attendee.focus || "",
        linkedin_url: attendee.linkedin_url || "",
        component_type: "attendess",
      } as Field));

    sections.push({
      section_id: `meeting_attendees_${sectionOrder}`,
      section_key: "meeting_attendees",
      section_title: "Meeting attendees",
      section_order: sectionOrder++,
      tab_id: "tab_1",
      fields: attendeeFields,
      suggestions: this.transformSuggestions(meetingAttendees.suggestions || []),
    });

    // 5. Rithum Aligns
    const rhithumAligns = data.rhithum_aligns || {};
    const alignsFields: Field[] = (Array.isArray(rhithumAligns.items) ? rhithumAligns.items : [])
      .filter((item) => item != null)
      .map((item) => ({
        icon: this.getIconForAlignsTitle(item.title || ""),
        title: item.title || "",
        description: item.description || "",
      alignment_id: item.id || "",
      component_type: "rithum_aligns",
      has_detail_view: true,
    } as AlignsField));

    sections.push({
      section_id: `rhithum_aligns_${sectionOrder}`,
      section_key: "rhithum_aligns",
      section_title: "How Rithum Aligns to Their Priorities",
      section_order: sectionOrder++,
      tab_id: "tab_1",
      fields: alignsFields,
      layout: "grid",
      suggestions: this.transformSuggestions(rhithumAligns.suggestions || []),
    });

    // 6. Leadership Team
    const leadershipTeam = data.leadership_team || {};
    const leadershipFields: Field[] = (Array.isArray(leadershipTeam.team) ? leadershipTeam.team : [])
      .filter((leader) => leader != null)
      .map((leader, idx) => ({
        name: leader.name || "",
        title: leader.designation || "",
        tenure: leader.tenure || "",
        insights: (Array.isArray(leader.highlights) ? leader.highlights : [])
          .filter((highlight) => highlight != null)
          .map((highlight, hIdx) => ({
            content: highlight.content || "",
            sources: (Array.isArray(highlight.sources) ? highlight.sources : [])
              .filter((source) => source != null)
              .map((source, sIdx) => ({
                url: source.url || "",
                title: source.name || "",
          source_id: `source_${sectionOrder}_${idx}_${hIdx}_${sIdx}`,
        })),
        insight_id: `insight_${sectionOrder}_${idx}_${hIdx}`,
      })),
      image_url: leader.img_url || null,
      person_id: `person_${sectionOrder}_${idx}`,
        focus_area: leader.focus || "",
        linkedin_url: leader.linkedin_url || "",
      component_type: "leadership",
      is_meeting_confirmed: false,
    } as LeadershipField));

    sections.push({
      section_id: `leadership_team_${sectionOrder}`,
      section_key: "leadership_team",
      section_title: "Leadership Team & Focus Areas",
      section_order: sectionOrder++,
      tab_id: "tab_1",
      fields: leadershipFields,
      suggestions: this.transformSuggestions(leadershipTeam.suggestions || []),
    });

    const marketplaceFields: Field[] = (Array.isArray(marketplacePresence.marketplaces) ? marketplacePresence.marketplaces : [])
    .filter((mp) => mp != null)
    .map((mp) => ({
      name: mp.platform || "",
    logo_url: null,
    component_type: "market_place",
      is_present: true,
      platform_meta: Array.isArray(mp.platform_meta) ? mp.platform_meta : [],
  } as MarketplaceField));

  sections.push({
    section_id: `marketplace_presence_${sectionOrder}`,
    section_key: "marketplace_presence",
    section_title: "Marketplace Search Results ",
    section_order: sectionOrder++,
    tab_id: "tab_1",
    fields: marketplaceFields,
    suggestions: this.transformSuggestions(marketplacePresence.suggestions || []),
  });

    return sections;
  }

  /**
   * Transform suggestions from new API format to old format.
   * Supports challenge/response (Detours) and description (Insight).
   */
  private static transformSuggestions(
    suggestions?: Array<{
      id?: string | number;
      title?: string;
      description?: string | null;
      challenge?: string | null;
      response?: string | null;
      theme?: string | null;
      is_liked?: boolean | null;
      is_disliked?: boolean | null;
      added_to_meeting?: boolean;
    }>
  ): Suggestion[] {
    if (!Array.isArray(suggestions)) return [];
    return suggestions
      .filter((suggestion) => suggestion != null)
      .map((suggestion, idx) => ({
        title: suggestion.title || "",
        actions: [
          {
            icon: "plus",
            type: "button",
            label: "Add to MAP",
            action: "add_to_meeting",
            variant: "primary",
            action_id: `action_add_${idx}`,
          },
          {
            icon: "thumbs-up",
            type: "icon_button",
            action: "like_suggestion",
            variant: "ghost",
            action_id: `action_like_${idx}`,
          },
          {
            icon: "thumbs-down",
            type: "icon_button",
            action: "dislike_suggestion",
            variant: "ghost",
            action_id: `action_dislike_${idx}`,
          },
        ],
        content: {
          text: suggestion.description ?? "",
          has_icon: true,
          icon_type: "lightbulb",
          challenge: suggestion.challenge ?? undefined,
          response: suggestion.response ?? undefined,
        },
        metadata: {
          is_liked: suggestion.is_liked ?? false,
          is_disliked: suggestion.is_disliked ?? false,
          added_to_meeting: suggestion.added_to_meeting ?? false,
        },
        id: String(suggestion.id ?? idx),
        challenge: suggestion.challenge ?? undefined,
        response: suggestion.response ?? undefined,
        description: suggestion.description ?? undefined,
        theme: suggestion.theme ?? undefined,
      }));
  }

  /**
   * Get icon name for aligns title
   */
  private static getIconForAlignsTitle(title: string): string {
    const titleLower = title.toLowerCase();
    // Data-led optimization - database with lightning bolt
    if (titleLower.includes("data") || titleLower.includes("optimization")) {
      return "database-zap";
    }
    // Mobile-first commerce & UX - smartphone icon
    if (titleLower.includes("mobile") || titleLower.includes("ux") || titleLower.includes("commerce")) {
      return "smartphone";
    }
    // Managed marketplace expansion - arrows pointing outwards
    if (titleLower.includes("marketplace") || titleLower.includes("expansion")) {
      return "move";
    }
    // Content & brand stewardship - document/page icon
    if (titleLower.includes("content") || titleLower.includes("brand") || titleLower.includes("stewardship")) {
      return "file-text";
    }
    // Evidence Proof - lightbulb
    if (titleLower.includes("evidence") || titleLower.includes("proof")) {
      return "lightbulb";
    }
    // Impact Prompt - target
    if (titleLower.includes("impact")) {
      return "target";
    }
    return "database-zap"; // default
  }

  /**
   * Update suggestion metadata (like, dislike, add to meeting)
   * @param suggestionId - The suggestion ID
   * @param metadata - The metadata to update
   * @returns Promise with updated suggestion metadata
   */
  static async updateSuggestionMetadata(
    suggestionId: string | number,
    metadata: {
      is_liked: boolean;
      is_disliked: boolean;
      added_to_meeting: boolean;
    }
  ): Promise<{
    id: number;
    title: string;
    description: string;
    is_liked: boolean;
    is_disliked: boolean;
    added_to_meeting: boolean;
  }> {
    const response = await clientApi
      .patch(`api/meetings/suggestions/${suggestionId}`, {
        json: metadata,
      })
      .json();

    return response as {
      id: number;
      title: string;
      description: string;
      is_liked: boolean;
      is_disliked: boolean;
      added_to_meeting: boolean;
    };
  }

  /**
   * Fetch signal details by ID
   * @param signalId - The signal ID
   * @returns Promise with signal details response
   */
  static async getSignalDetails(signalId: number): Promise<{
    id: number;
    title: string;
    description: string;
    sources: Array<{
      name: string;
      url: string;
      type: string;
    }>;
  }> {
    const response = await clientApi
      .get(`api/meetings/signals_priorities/${signalId}`)
      .json();

    return response as {
      id: number;
      title: string;
      description: string;
      sources: Array<{
        name: string;
        url: string;
        type: string;
      }>;
    };
  }

  /**
   * Fetch alignment details by ID
   * @param alignmentId - The alignment ID (can be string or number)
   * @returns Promise with alignment details response
   */
  static async getAlignmentDetails(alignmentId: string | number): Promise<{
    id: number;
    title: string;
    description: string;
    explanation: string;
    capabilities: string | null;
    sources: Array<{
      name: string;
      url: string;
      type: string;
    }> | null;
  }> {
    const response = await clientApi
      .get(`api/meetings/rhithum_aligns/${alignmentId}`)
      .json();

    return response as {
      id: number;
      title: string;
      description: string;
      explanation: string;
      capabilities: string | null;
      sources: Array<{
        name: string;
        url: string;
        type: string;
      }> | null;
    };
  }

  /**
   * Fetch suggestion details by ID
   * @param suggestionId - The suggestion ID (can be string or number)
   * @returns Promise with suggestion details response
   */
  static async getSuggestionDetails(suggestionId: string | number): Promise<{
    id: number;
    title: string;
    description: string;
    challenge?: string | null;
    response?: string | null;
    explanation: string;
    reasoning?: string | null;
  }> {
    const response = await clientApi
      .get(`api/meetings/suggestions/${suggestionId}/`)
      .json();

    return response as {
      id: number;
      title: string;
      description: string;
      challenge?: string | null;
      response?: string | null;
      explanation: string;
      reasoning?: string | null;
    };
  }
}

